import { transitNotificationsUpdateById } from '@/services/api-transit/transitNotifications';
import { CheckCircleOutlined } from '@ant-design/icons';
import { Button, List, message } from 'antd';
import classNames from 'classnames';
import moment from 'moment';
import React from 'react';
import styles from './NoticeList.less';

export type NoticeIconTabProps = {
  count?: number;
  showClear?: boolean;
  showViewMore?: boolean;
  style?: React.CSSProperties;
  title: string;
  tabKey: API.NoticeIconItemType;
  onClick?: (item: API.NoticeIconItem) => void;
  onClear?: () => void;
  emptyText?: string;
  clearText?: string;
  viewMoreText?: string;
  list: API.NoticeIconItem[];
  onViewMore?: (e: any) => void;
};

const NoticeList: React.FC<NoticeIconTabProps> = ({
  list = [],
  onClick,
  onClear,
  title,
  onViewMore,
  emptyText,
  showClear = true,
  clearText,
  viewMoreText,
  showViewMore = false,
}) => {
  if (!list || list.length === 0) {
    return (
      <div className={styles.notFound}>
        <img
          src="https://gw.alipayobjects.com/zos/rmsportal/sAuJeJzSKbUmHfBQRzmZ.svg"
          alt="not found"
        />
        <div>{emptyText}</div>
      </div>
    );
  }

  const markAsRead = async (item: API.NoticeIconItem) => {
    try {
      await transitNotificationsUpdateById(
        { id: item.id! },
        { status: 'read' },
      );
      onClick?.({ ...item, read: true });
    } catch (err) {
      message.error('Không thể cập nhật trạng thái thông báo');
    }
  };

  const handleClickItem = (item: API.NoticeIconItem) => {
    // Chỉ đơn hàng mới (message/info) mới tự đánh dấu khi click item
    if (item.type === 'message') {
      markAsRead(item);
      return;
    }
    onClick?.(item);
  };

  const handleAcknowledge = (e: React.MouseEvent, item: API.NoticeIconItem) => {
    e.stopPropagation();
    // Sự cố nghiêm trọng: chỉ đánh dấu khi bấm nút
    markAsRead(item);
  };

  return (
    <div>
      <List<API.NoticeIconItem>
        className={styles.list}
        dataSource={list}
        renderItem={(item, i) => {
          const isUrgent = item.status === 'urgent';
          const isRead = item.read;
          const itemCls = classNames(styles.item, {
            [styles.urgent]: isUrgent,
            [styles.read]: isRead,
          });

          return (
            <div
              key={item.key || i}
              className={itemCls}
              onClick={() => {
                handleClickItem(item);
              }}
            >
              <div className={styles.itemContent}>
                <div className={styles.itemHeader}>
                  <div className={styles.itemTitle}>{item.title}</div>
                  <div className={styles.itemTime}>
                    <span>
                      {item.datetime
                        ? moment(item.datetime).format('DD/MM • HH:mm')
                        : ''}
                    </span>
                  </div>
                </div>

                <div className={styles.itemDescription}>
                  {item.description && (
                    <p className={styles.descriptionText}>{item.description}</p>
                  )}
                </div>

                {isUrgent && !isRead && (
                  <>
                    <div className={styles.divider} />
                    <div className={styles.urgentAction}>
                      <Button
                        type="primary"
                        danger
                        ghost
                        size="small"
                        icon={<CheckCircleOutlined />}
                        onClick={(e) => handleAcknowledge(e, item)}
                      >
                        Đã nhận biết
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          );
        }}
      />
      {list && list.length > 0 && (
        <div className={styles.footer}>
          <p className={styles.footerText}>Đã tải hết thông báo</p>
        </div>
      )}
      {(showClear || showViewMore) && (
        <div className={styles.bottomBar}>
          {showClear ? (
            <div className={styles.bottomAction} onClick={onClear}>
              {clearText} {title}
            </div>
          ) : null}
          {showViewMore ? (
            <div
              className={styles.bottomAction}
              onClick={(e) => {
                if (onViewMore) {
                  onViewMore(e);
                }
              }}
            >
              {viewMoreText}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
};

export default NoticeList;
